import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/leases`;

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 14px', color: 'var(--text-hint)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px' };

export default function LeaseAccounting() {
  const { entity } = useGlobalEntity();
  const [leases, setLeases] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({
    lease_number: '', lease_type: 'OPERATING', asset_type: 'REAL_ESTATE',
    description: '', lessor_name: '', commencement_date: '', end_date: '',
    monthly_payment: '', discount_rate: '0.05', framework: 'IFRS16', entity_code: '',
    // M25A.1
    annual_escalation_pct: '0', free_months_start: '0',
    // M25A.3
    initial_direct_costs: '0', lease_incentives: '0', prepaid_payments: '0', aro_amount: '0',
    // M25A.4
    is_short_term: false, is_low_value: false,
    // M25A.2 (optional classification inputs)
    useful_life_months: '', asset_fair_value: '',
    ownership_transfer: '', bargain_purchase: '', is_specialized: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tab, setTab] = useState<'leases' | 'schedule'>('leases');

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(API, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setLeases(d.items || []); }
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    const res = await authFetch(`${API}/${id}`);
    if (res.ok) { setDetail(await res.json()); setExpandedId(id); setTab('schedule'); }
  };

  const handleCreate = async () => {
    const _toNum = (v: any) => v === '' || v == null ? undefined : (parseFloat(v) || 0);
    const _toInt = (v: any) => v === '' || v == null ? undefined : (parseInt(v) || 0);
    const _toBoolMaybe = (v: any) => v === '' || v == null ? undefined : (v === 'true' || v === true);
    const payload: any = {
      lease_number: form.lease_number, lease_type: form.lease_type,
      asset_type: form.asset_type, description: form.description,
      lessor_name: form.lessor_name, commencement_date: form.commencement_date,
      end_date: form.end_date, framework: form.framework, entity_code: form.entity_code,
      monthly_payment: parseFloat(form.monthly_payment) || 0,
      discount_rate: parseFloat(form.discount_rate) || 0.05,
      annual_escalation_pct: parseFloat(form.annual_escalation_pct) || 0,
      free_months_start: parseInt(form.free_months_start) || 0,
      initial_direct_costs: parseFloat(form.initial_direct_costs) || 0,
      lease_incentives: parseFloat(form.lease_incentives) || 0,
      prepaid_payments: parseFloat(form.prepaid_payments) || 0,
      aro_amount: parseFloat(form.aro_amount) || 0,
      is_short_term: !!form.is_short_term, is_low_value: !!form.is_low_value,
    };
    // M25A.2 classification inputs (optional)
    const ulm = _toInt(form.useful_life_months);
    const afv = _toNum(form.asset_fair_value);
    const ot = _toBoolMaybe(form.ownership_transfer);
    const bp = _toBoolMaybe(form.bargain_purchase);
    const sp = _toBoolMaybe(form.is_specialized);
    if (ulm !== undefined) payload.useful_life_months = ulm;
    if (afv !== undefined) payload.asset_fair_value = afv;
    if (ot !== undefined) payload.ownership_transfer = ot;
    if (bp !== undefined) payload.bargain_purchase = bp;
    if (sp !== undefined) payload.is_specialized = sp;
    const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) {
      const result = await res.json();
      const cls = result.classification_test_result;
      const msg = cls ? `Lease created — classified ${cls.classification}` : 'Lease created';
      showToast(msg, true);
      setShowCreate(false); load();
    } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleAction = async (id: number, action: string) => {
    const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST' });
    if (res.ok) { showToast(`Lease ${action}`, true); load(); }
    else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  return (
    <PageLayout
      title="Lease Accounting"
      subtitle="IFRS 16 / ASC 842 -- right-of-use assets, lease liabilities, amortization"
      action={{ label: '+ New Lease', onClick: () => setShowCreate(true) }}
    >
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: toast.ok ? '#22c55e' : '#ef4444', color: '#fff' }}>{toast.msg}</div>}

      {summary && (
        <KpiGrid items={[
          { label: 'Total Leases', value: String(summary.total_leases) },
          { label: 'Active', value: String(summary.active), color: '#22c55e' },
          { label: 'ROU Assets', value: formatAmountCompact(summary.total_rou_assets), color: 'var(--accent)' },
          { label: 'Lease Liabilities', value: formatAmountCompact(summary.total_lease_liability), color: '#f59e0b' },
          { label: 'Monthly Payments', value: formatAmountCompact(summary.total_monthly_payments) },
        ]} columns={5} />
      )}

      <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['Lease #', 'Type', 'Lessor', 'Start', 'End', 'Monthly', 'ROU Asset', 'Liability', 'Framework', 'Status', 'Actions'].map(h => (
              <th key={h} style={{ ...thStyle, textAlign: 'left' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</td></tr> :
            leases.length === 0 ? <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>No leases. Create one to get started.</td></tr> :
            leases.map(l => (
              <>
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }} onClick={() => loadDetail(l.id)}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{l.lease_number}</td>
                  <td style={tdStyle}>{l.lease_type}</td>
                  <td style={tdStyle}>{l.lessor_name || '-'}</td>
                  <td style={tdStyle}>{l.commencement_date}</td>
                  <td style={tdStyle}>{l.end_date}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(l.monthly_payment)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(l.rou_asset_current)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(l.lease_liability_current)}</td>
                  <td style={tdStyle}>{l.framework}</td>
                  <td style={tdStyle}><StatusBadge status={l.lifecycle_status} /></td>
                  <td style={tdStyle}>
                    {l.lifecycle_status === 'DRAFT' && <ActionButton label="Activate" onClick={e => { e.stopPropagation(); handleAction(l.id, 'activate'); }} variant="success" size="sm" />}
                  </td>
                </tr>
                {expandedId === l.id && detail?.schedule && (
                  <tr key={`${l.id}-sched`}><td colSpan={11} style={{ padding: 0 }}>
                    <div style={{ padding: '12px 20px', background: 'var(--bg-hover)', maxHeight: 300, overflow: 'auto' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>AMORTIZATION SCHEDULE ({detail.schedule.length} periods)</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>{['Period', 'Payment', 'Interest', 'Principal', 'Amortization', 'Liability', 'ROU Asset', 'Status'].map(h => <th key={h} style={{ ...thStyle, textAlign: 'right' }}>{h}</th>)}</tr></thead>
                        <tbody>{detail.schedule.map((s: any, i: number) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            <td style={{ ...tdStyle, textAlign: 'left' }}>{s.period}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.payment)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.interest)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.principal)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.amortization)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.closing_liability)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(s.closing_rou)}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}><StatusBadge status={s.status} /></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </td></tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Lease">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            ['Lease Number', 'lease_number', 'LSE-001'],
            ['Lessor Name', 'lessor_name', 'ABC Realty'],
            ['Description', 'description', 'Office space'],
            ['Commencement Date *', 'commencement_date', '2026-01-01'],
            ['End Date *', 'end_date', '2031-12-31'],
            ['Monthly Payment *', 'monthly_payment', '5000'],
            ['Discount Rate', 'discount_rate', '0.05'],
            ['Entity Code', 'entity_code', 'USA'],
          ].map(([label, field, ph]) => (
            <div key={field as string}>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
              <input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} />
            </div>
          ))}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Lease Type</label>
            <select style={inputStyle} value={form.lease_type} onChange={e => setForm({ ...form, lease_type: e.target.value })}>
              <option value="OPERATING">Operating</option><option value="FINANCE">Finance</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Framework</label>
            <select style={inputStyle} value={form.framework} onChange={e => setForm({ ...form, framework: e.target.value })}>
              <option value="IFRS16">IFRS 16</option><option value="ASC842">ASC 842</option>
            </select>
          </div>
        </div>
        {/* M25A advanced section */}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', padding: 0 }}
          >
            {showAdvanced ? '▼' : '▶'} Advanced — Escalation, Initial Measurement, Classification (M25A)
          </button>
          {showAdvanced && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Payment escalation + free rent (M25A.1)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                {[
                  ['Annual Escalation %', 'annual_escalation_pct', '0.05 for 5%'],
                  ['Free Months at Start', 'free_months_start', '0'],
                ].map(([label, field, ph]) => (
                  <div key={field as string}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
                    <input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Initial measurement (M25A.3 — added to / subtracted from ROU)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                {[
                  ['Initial Direct Costs (+ROU)', 'initial_direct_costs', '0'],
                  ['Lease Incentives (−ROU)', 'lease_incentives', '0'],
                  ['Prepaid Payments (+ROU)', 'prepaid_payments', '0'],
                  ['ARO / Restoration (+ROU)', 'aro_amount', '0'],
                ].map(([label, field, ph]) => (
                  <div key={field as string}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
                    <input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Exemptions (M25A.4 — skips ROU/liability)</div>
              <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!form.is_short_term} onChange={e => setForm({ ...form, is_short_term: e.target.checked })} />
                  Short-term (≤12 months, no purchase option)
                </label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!form.is_low_value} onChange={e => setForm({ ...form, is_low_value: e.target.checked })} />
                  Low-value (IFRS 16 only)
                </label>
              </div>

              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Classification test inputs (M25A.2 — supply any to auto-classify)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['Useful Life (months)', 'useful_life_months', '120'],
                  ['Asset Fair Value', 'asset_fair_value', '250000'],
                ].map(([label, field, ph]) => (
                  <div key={field as string}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
                    <input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} />
                  </div>
                ))}
                {[
                  ['Ownership Transfer?', 'ownership_transfer'],
                  ['Bargain Purchase?', 'bargain_purchase'],
                  ['Specialized Asset?', 'is_specialized'],
                ].map(([label, field]) => (
                  <div key={field as string}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
                    <select title={label as string} aria-label={label as string} style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })}>
                      <option value="">(skip)</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <ActionButton label="Create Lease" onClick={handleCreate} />
        </div>
      </Modal>
    </PageLayout>
  );
}
